import React from 'react';
import { motion } from 'framer-motion';
import { Award, Building, Users, Handshake } from 'lucide-react';

const milestones = [
  {
    year: '1966',
    title: 'Gründung',
    description: 'Die Brüder Karl Hein und Bernd Daniels gründen die Gebrüder Daniels GbR. Seit der Gründung ist das Unternehmen Mitglied der Innung und steht für zuverlässige Elektroarbeit in Bonn und der Region.',
    icon: Building,
  },
  {
    year: '1970er',
    title: 'Bauboom & Spezialisierung',
    description: 'Durch den Bauboom, Neubauten und die Zusammenarbeit mit Wohnungsbaugesellschaften wächst das Unternehmen weiter. In dieser Zeit gewinnen Nachtspeicherheizungen an Bedeutung, unter anderem durch Sondertarife der Energieversorger. Daniels Elektroinstallationen spezialisiert sich zunehmend auf diesen Bereich.',
    icon: Award,
  },
  {
    year: '1980er / 1990er',
    title: 'Umfirmierung & Erweiterung',
    description: 'Aus der Gebrüder Daniels GbR geht 1984 die K.H. und B. Daniels Elektroinstallationen GmbH hervor. In den folgenden Jahren wird das Leistungsspektrum erweitert – unter anderem um Sat-Anlagen, BK-Anlagen und moderne Kommunikationsanlagen.',
    icon: Users,
  },
  {
    year: '2000er',
    title: 'Moderne Technik',
    description: 'Mit der Digitalisierung erweitern wir unser Portfolio um moderne Video-Sprechanlagen, digitale Empfangstechnik und zeitgemäße Kommunikationslösungen für Privat- und Geschäftskunden.',
    icon: Users,
  },
  {
    year: 'Heute',
    title: 'Tradition trifft Innovation',
    description: 'Fast 60 Jahre Erfahrung im Elektrohandwerk – und immer am Puls der Zeit. Aktiv in Bonn, im Rhein-Sieg-Kreis und im Raum Köln.',
    icon: Handshake,
  },
];

export default function HistorienSection() {
  return (
    <section id="historie" data-testid="historie-section" className="py-24 sm:py-32 bg-white relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#2D5A27]/5 rounded-full translate-y-1/2 -translate-x-1/3" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 sm:mb-20"
        >
          <span className="text-[#10B981] font-['Manrope'] text-sm font-semibold tracking-[0.2em] uppercase">
            Unsere Geschichte
          </span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-[#1A3A22] tracking-tight mt-3 mb-4">
            Seit 1966. <span className="text-[#2D5A27]">Aus Tradition gut.</span>
          </h2>
          <p className="font-['Manrope'] text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
            Fast 60 Jahre Erfahrung im Elektrohandwerk. Gegründet in Bonn, verwurzelt in der Region
            und seit der ersten Stunde Mitglied der Innung.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#2D5A27] via-[#10B981] to-[#2D5A27]/20" />

          <div className="space-y-12 md:space-y-0">
            {milestones.map((milestone, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                data-testid={`historie-milestone-${idx}`}
                className={`relative md:flex items-center md:mb-16 last:md:mb-0 ${
                  idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Content */}
                <div className={`md:w-[calc(50%-2rem)] ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <span className="font-['Outfit'] text-[#10B981] text-3xl font-extrabold block mb-2">
                    {milestone.year}
                  </span>
                  <h3 className="font-['Outfit'] text-xl font-bold text-[#1A3A22] mb-2">
                    {milestone.title}
                  </h3>
                  <p className="font-['Manrope'] text-gray-600 text-sm leading-relaxed">
                    {milestone.description}
                  </p>
                </div>

                {/* Center dot */}
                <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-12 h-12 bg-[#2D5A27] border-4 border-white rounded-full items-center justify-center z-10 shadow-lg">
                  <milestone.icon className="w-5 h-5 text-white" strokeWidth={2} />
                </div>

                {/* Spacer for the other side */}
                <div className="hidden md:block md:w-[calc(50%-2rem)]" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Memberships bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-16 bg-[#F4F8F4] p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12"
          data-testid="memberships-bar"
        >
          <div className="flex items-center gap-3">
            <Award className="w-6 h-6 text-[#2D5A27]" strokeWidth={1.5} />
            <span className="font-['Manrope'] text-[#1A3A22] font-medium text-sm">Innung Köln</span>
          </div>
          <div className="hidden sm:block w-[1px] h-6 bg-[#2D5A27]/20" />
          <div className="flex items-center gap-3">
            <Award className="w-6 h-6 text-[#2D5A27]" strokeWidth={1.5} />
            <span className="font-['Manrope'] text-[#1A3A22] font-medium text-sm">Kreishandwerkerschaft Bonn/Rhein-Sieg</span>
          </div>
          <div className="hidden sm:block w-[1px] h-6 bg-[#2D5A27]/20" />
          <div className="flex items-center gap-3">
            <Award className="w-6 h-6 text-[#2D5A27]" strokeWidth={1.5} />
            <span className="font-['Manrope'] text-[#1A3A22] font-medium text-sm">Seit Gründung Mitglied der Innung</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
